import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  FileDto,
  FileId,
  PropertyDocumentDto,
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyDto,
  PropertyVerificationStatus,
  UserApplications,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, of, tap } from 'rxjs';

import { PropertyDocumentItemComponent } from './item/property-document-item.component';

@Component({
  selector: 'etxe-property-documents-edit',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    PropertyDocumentItemComponent,
  ],
  templateUrl: './property-documents-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDocumentsEditComponent {
  private readonly api = inject(ApiService);
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStore = inject(ApplicationStore);
  private readonly userStore = inject(UserStore);

  @ViewChild('genericFileInput') genericFileInput?: ElementRef<HTMLInputElement>;

  property = input.required<PropertyDto>();
  editable = input<boolean>(true);
  propertyUpdated = output<PropertyDto>();

  isUploading = signal<boolean>(false);
  uploadingType = signal<PropertyDocumentType | null>(null);
  selectedGenericType = signal<PropertyDocumentType>(PropertyDocumentType.OTHER);
  isDeletingId = signal<string | null>(null);
  isHistoryExpanded = signal<boolean>(false);

  readonly isAdmin = computed(
    () => this.appStore.name() === UserApplications.ADMIN && this.userStore.isAdmin(),
  );

  PropertyVerificationStatus = PropertyVerificationStatus;
  PropertyDocumentStatus = PropertyDocumentStatus;
  PropertyDocumentType = PropertyDocumentType;

  documents = computed<PropertyDocumentDto[]>(() => {
    return this.property()?.documents || [];
  });

  ownershipDocs = computed<PropertyDocumentDto[]>(() => {
    return this.documents().filter((d) => d.type === PropertyDocumentType.OWNERSHIP);
  });

  latestOwnershipDoc = computed<PropertyDocumentDto | undefined>(() => {
    const docs = this.ownershipDocs();
    return docs.length > 0 ? docs[docs.length - 1] : undefined;
  });

  previousOwnershipDocs = computed<PropertyDocumentDto[]>(() => {
    const docs = this.ownershipDocs();
    if (docs.length <= 1) return [];
    return docs.slice(0, docs.length - 1).reverse();
  });

  otherDocuments = computed<PropertyDocumentDto[]>(() => {
    return this.documents().filter((d) => d.type !== PropertyDocumentType.OWNERSHIP);
  });

  isOwnershipPendingReview = computed(
    () => this.latestOwnershipDoc()?.status === PropertyDocumentStatus.PENDING_REVIEW,
  );

  verificationStatus = computed<PropertyVerificationStatus>(() => {
    if (this.property()?.verification?.owner === PropertyVerificationStatus.VERIFIED) {
      return PropertyVerificationStatus.VERIFIED;
    }
    const latest = this.latestOwnershipDoc();
    if (!latest) {
      return PropertyVerificationStatus.UNVERIFIED;
    }
    if (latest.status === PropertyDocumentStatus.VERIFIED) {
      return PropertyVerificationStatus.VERIFIED;
    }
    if (latest.status === PropertyDocumentStatus.REJECTED) {
      return PropertyVerificationStatus.REJECTED;
    }
    return PropertyVerificationStatus.UNVERIFIED;
  });

  onFileSelected(event: Event, type: PropertyDocumentType): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadDocument(file, type);
      input.value = '';
    }
  }

  triggerGenericUpload(type: PropertyDocumentType): void {
    this.selectedGenericType.set(type);
    this.genericFileInput?.nativeElement.click();
  }

  onGenericFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadDocument(file, this.selectedGenericType());
      input.value = '';
    }
  }

  uploadDocument(file: File, type: PropertyDocumentType, label?: string): void {
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) {
      this.alertSnackbar.error({
        message: 'Le fichier est trop volumineux (maximum 20 Mo)',
      });
      return;
    }

    this.isUploading.set(true);
    this.uploadingType.set(type);
    const propertyId = this.property()._id;

    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .uploadDocument(propertyId, file, type, label)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedProperty) => {
          this.isUploading.set(false);
          this.uploadingType.set(null);
          this.propertyUpdated.emit(updatedProperty);
          this.alertSnackbar.success({ message: 'Document ajouté avec succès !' });
        }),
        catchError((err) => {
          console.error('Failed to upload document:', err);
          this.isUploading.set(false);
          this.uploadingType.set(null);
          this.alertSnackbar.error({
            message: 'Erreur lors du téléversement du document',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  isDocumentVerified(doc?: PropertyDocumentDto): boolean {
    if (!doc) return false;
    if (doc.type === PropertyDocumentType.OWNERSHIP) {
      return (
        doc.status === PropertyDocumentStatus.VERIFIED ||
        this.verificationStatus() === PropertyVerificationStatus.VERIFIED
      );
    }
    return doc.status === PropertyDocumentStatus.VERIFIED;
  }

  isDocumentDeletable(doc?: PropertyDocumentDto): boolean {
    if (!doc) return false;
    if (doc.status === PropertyDocumentStatus.VERIFIED || doc.status === PropertyDocumentStatus.REJECTED) {
      return false;
    }
    if (
      doc.type === PropertyDocumentType.OWNERSHIP &&
      this.verificationStatus() === PropertyVerificationStatus.VERIFIED
    ) {
      return false;
    }
    return true;
  }

  deleteDocument(doc: PropertyDocumentDto): void {
    if (!this.isDocumentDeletable(doc)) {
      this.alertSnackbar.error({
        message: 'Un document validé ou refusé ne peut pas être supprimé',
      });
      return;
    }

    const fileId = this.getFileId(doc);
    if (!fileId) return;

    if (!confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    this.isDeletingId.set(fileId.toString());
    const propertyId = this.property()._id;

    this.api
      .business()
      .etxe()
      .properties()
      .owner()
      .deleteDocument(propertyId, fileId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updatedProperty) => {
          this.isDeletingId.set(null);
          this.propertyUpdated.emit(updatedProperty);
          this.alertSnackbar.success({ message: 'Document supprimé' });
        }),
        catchError((err) => {
          console.error('Failed to delete document:', err);
          this.isDeletingId.set(null);
          this.alertSnackbar.error({
            message: 'Erreur lors de la suppression',
          });
          return of(null);
        }),
      )
      .subscribe();
  }

  getFileId(doc?: PropertyDocumentDto): FileId | undefined {
    if (!doc?.file) return undefined;
    if (typeof doc.file === 'object' && '_id' in doc.file) {
      return doc.file._id as FileId;
    }
    return doc.file as unknown as FileId;
  }

  getFileName(doc: PropertyDocumentDto): string {
    if (doc.file && typeof doc.file === 'object' && 'filename' in doc.file) {
      return (doc.file as FileDto).filename;
    }
    return doc.label || 'Document';
  }

  getFileSize(doc: PropertyDocumentDto): number | undefined {
    if (doc.file && typeof doc.file === 'object' && 'size' in doc.file) {
      return (doc.file as FileDto).size;
    }
    return undefined;
  }

  getFileUrl(doc: PropertyDocumentDto): string | undefined {
    if (doc.url) return doc.url;
    if (doc.file && typeof doc.file === 'object' && 'url' in doc.file) {
      return (doc.file as FileDto).url;
    }
    return undefined;
  }

  getDocumentTypeLabel(type: PropertyDocumentType): string {
    switch (type) {
      case PropertyDocumentType.OWNERSHIP:
        return 'Titre / Taxe foncière';
      case PropertyDocumentType.DPE:
        return 'Diagnostic DPE / DDT';
      case PropertyDocumentType.CO_OWNERSHIP:
        return 'Copropriété / PV d’AG';
      case PropertyDocumentType.FLOOR_PLAN:
        return 'Plan du bien';
      case PropertyDocumentType.OTHER:
      default:
        return 'Autre document';
    }
  }

  getDocumentTypeIcon(type: PropertyDocumentType): string {
    switch (type) {
      case PropertyDocumentType.OWNERSHIP:
        return 'shield-check';
      case PropertyDocumentType.DPE:
        return 'gauge';
      case PropertyDocumentType.CO_OWNERSHIP:
        return 'building';
      case PropertyDocumentType.FLOOR_PLAN:
        return 'map';
      case PropertyDocumentType.OTHER:
      default:
        return 'file-text';
    }
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} Ko`;
    return `${(kb / 1024).toFixed(1)} Mo`;
  }
}
